// Los albaranes (packing slips) de cada fila de línea de pedido.
//
// LA REGLA DE ESTE FICHERO, Y VIVE SÓLO AQUÍ: cada fila enseña **el albarán que le
// toca a esa fila**, no todos los de su línea.
//
//   fila CON factura   → los albaranes de ese par (línea × factura)
//   fila REMANENTE     → los albaranes de la línea que aún no se han facturado
//
// Ejemplo: línea de 1.000 uds, 600 facturadas en la factura A, 800 servidas. La
// vista de líneas devuelve (línea, A, 600) + (línea, —, 400); los albaranes son
// alb1 (600, factura A) y alb2 (200, sin facturar). La fila de A enseña alb1 y la
// remanente enseña alb2. Así la cantidad del albarán CUADRA con la de la fila, y
// una celda vacía significa «esta parte no se ha servido», que es la verdad.
//
// POR QUÉ EN UN SOLO SITIO: la pantalla y el Excel consumen esta misma función. Que
// pantalla y Excel dijeran cosas distintas de la misma magnitud es exactamente la
// queja que trajo esta columna —el cliente preguntó por qué «Order Qty» se llamaba
// «Qty ordered»—, y no se va a repetir con el albarán. Hay un test que compara la
// celda del Excel contra lo que devuelve esta función.
import type { AlbaranLinea, PedidoVentaLinea } from '@/types'

/** Los albaranes atribuibles a una fila, ya deduplicados y ordenados. */
export interface ResumenAlbaranes {
  albaranes: string[]
  /** Las cargas en las que salieron esos albaranes, deduplicadas.
   *
   *  Sale del albarán y no de `preparacion_cargas_lineas`: aquélla da TODAS las
   *  cargas de la línea —hasta 19 en un pedido medido— y no sabe cuál corresponde
   *  a esta factura. Por eso normalmente hay una sola. Vacío significa que el
   *  envío NO salió por una carga, no que se desconozca. */
  cargas: string[]
  /** Unidades servidas sumadas. PUEDE SER NEGATIVA: son abonos, y se pintan. */
  qty: number
  /** La primera fecha de albarán del grupo. Sin centinelas: la vista normaliza el
   *  `1900-01-01` de D365 a vacío, así que aquí una fecha o no hay fecha. */
  fechaAlbaran?: string
}

/** Cubo para las filas SIN factura. Cadena vacía y no `undefined` para que la clave
 *  del mapa sea siempre un string. */
const SIN_FACTURA = ''

function clave(inventtransid: string, factura?: string): string {
  return `${inventtransid}|${factura ?? SIN_FACTURA}`
}

function acumular(mapa: Map<string, ResumenAlbaranes>, k: string, a: AlbaranLinea) {
  const previo = mapa.get(k)
  if (!previo) {
    mapa.set(k, {
      albaranes: [a.albaran],
      cargas: a.carga ? [a.carga] : [],
      qty: a.qty,
      fechaAlbaran: a.fechaAlbaran,
    })
    return
  }
  if (!previo.albaranes.includes(a.albaran)) previo.albaranes.push(a.albaran)
  if (a.carga && !previo.cargas.includes(a.carga)) previo.cargas.push(a.carga)
  previo.qty += a.qty
  // La más antigua: es la primera entrega de ese grupo.
  if (a.fechaAlbaran && (!previo.fechaAlbaran || a.fechaAlbaran < previo.fechaAlbaran)) {
    previo.fechaAlbaran = a.fechaAlbaran
  }
}

/** Índice por (línea × factura). Las filas sin `inventtransid` se descartan: sin
 *  esa clave no hay línea a la que atribuirlas, igual que en `agruparPorTransid`. */
export function agruparAlbaranes(
  filas: AlbaranLinea[],
): Map<string, ResumenAlbaranes> {
  const mapa = new Map<string, ResumenAlbaranes>()
  for (const a of filas) {
    if (!a.inventtransid || !a.albaran) continue
    acumular(mapa, clave(a.inventtransid, a.factura), a)
  }
  for (const r of mapa.values()) { r.albaranes.sort(); r.cargas.sort() }
  return mapa
}

/**
 * LA REGLA DE FILA. Devuelve los albaranes que le tocan a esta fila concreta.
 *
 * `undefined` cuando esa parte de la línea no se ha servido — que es distinto de
 * «no lo sabemos», y por eso la celda lo explica en su tooltip.
 */
export function albaranesDeFila(
  indice: Map<string, ResumenAlbaranes>,
  l: PedidoVentaLinea,
): ResumenAlbaranes | undefined {
  if (!l.inventtransid) return undefined
  // `factura` viene con espacios en algunas filas de origen; se normaliza igual
  // que en el resto de la pantalla (`l.factura?.trim()`).
  const factura = l.factura?.trim() || undefined
  return indice.get(clave(l.inventtransid, factura))
}

/** Todos los albaranes de una línea, sin importar la factura. NO lo usa la tabla
 *  —eso rompería la regla de fila—, sólo el resumen del pedido y las pruebas. */
export function albaranesDeLinea(
  filas: AlbaranLinea[],
  inventtransid: string,
): string[] {
  const vistos = new Set<string>()
  for (const a of filas) {
    if (a.inventtransid === inventtransid && a.albaran) vistos.add(a.albaran)
  }
  return [...vistos].sort()
}

/**
 * Centinela de la asimetría entre fuentes.
 *
 * La vista de albaranes es LIVE y `planificacion_pedidos_ventas_lineas` es una
 * tabla que se reconstruye A MANO, así que el albarán puede ir por delante de la
 * línea. No produce cifras equivocadas —el cruce lo conduce la línea, de modo que
 * un albarán huérfano simplemente no se pinta—, pero sí produce un hueco que nadie
 * vería. Esto lo hace ruidoso.
 *
 * Devuelve cuántas filas de albarán no encontraron su línea.
 */
export function albaranesHuerfanos(
  filas: AlbaranLinea[],
  lineas: PedidoVentaLinea[],
): number {
  const conocidas = new Set(
    lineas.map(l => l.inventtransid).filter(Boolean) as string[],
  )
  if (conocidas.size === 0) return 0
  let n = 0
  for (const a of filas) {
    if (a.inventtransid && !conocidas.has(a.inventtransid)) n += 1
  }
  return n
}
